import { useMutation, useQuery } from '@tanstack/react-query';
import { Cpu, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, Card, CardContent, CardHeader, CardTitle, Select } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { DeviceItem, Paginated, TicketDetail } from '@/lib/types';

const SOURCE_LABEL: Record<string, string> = { NETDOC: 'aus NetDoc', MONITORING: 'aus Monitoring' };

/** Geräte am Ticket: automatisch aus NetDoc übernommen oder von Hand aus den Geräten des Kunden gewählt. */
export function TicketDevicesCard({ ticket, canUpdate, onChange }: { ticket: TicketDetail; canUpdate: boolean; onChange: () => void }) {
  const { can } = useAuth();
  const [pick, setPick] = useState('');
  const customerId = ticket.customer?.id;
  const { data: devices } = useQuery({
    queryKey: ['devices', 'customer', customerId],
    queryFn: () => api<Paginated<DeviceItem>>(`/devices?customerId=${customerId}&pageSize=100`),
    enabled: Boolean(customerId) && canUpdate && can('devices.read'),
  });
  const onError = (e: unknown) => toast.error(errorMessage(e));
  const add = useMutation({
    mutationFn: (deviceId: string) => api(`/tickets/${ticket.id}/devices`, { method: 'POST', body: { deviceId } }),
    onSuccess: () => {
      setPick('');
      onChange();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (deviceId: string) => api(`/tickets/${ticket.id}/devices/${deviceId}`, { method: 'DELETE' }), onSuccess: onChange, onError });
  const candidates = devices?.items.filter((d) => !ticket.devices.some((x) => x.id === d.id)) ?? [];
  const linkable = can('devices.read');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Geräte</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2">
        {ticket.devices.length === 0 && <p className="text-xs text-muted-foreground">{customerId ? 'Kein Gerät verknüpft.' : 'Für Geräte bitte zuerst einen Kunden zuordnen.'}</p>}
        {ticket.devices.map((d) => (
          <div key={d.id} className="flex items-start gap-2 text-sm">
            <Cpu className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              {linkable ? (
                <Link to={`/devices/${d.id}`} className="font-medium hover:underline">
                  {d.manufacturer} {d.model}
                </Link>
              ) : (
                <span className="font-medium">
                  {d.manufacturer} {d.model}
                </span>
              )}
              <p className="truncate text-xs text-muted-foreground">
                {[d.deviceType, d.inventoryNumber, d.serialNumber && `SN ${d.serialNumber}`].filter(Boolean).join(' · ')}
              </p>
            </div>
            {SOURCE_LABEL[d.source] && (
              <Badge variant="outline" className="shrink-0">
                {SOURCE_LABEL[d.source]}
              </Badge>
            )}
            {canUpdate && (
              <Button type="button" variant="ghost" size="icon" className="size-7 shrink-0" aria-label={`${d.manufacturer} ${d.model} vom Ticket lösen`} onClick={() => remove.mutate(d.id)}>
                <X />
              </Button>
            )}
          </div>
        ))}
        {canUpdate && customerId && candidates.length > 0 && (
          <div className="flex gap-2 pt-1">
            <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Gerät des Kunden wählen" className="h-8">
              <option value="">Gerät des Kunden wählen …</option>
              {candidates.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.manufacturer} {d.model}
                  {d.inventoryNumber ? ` (${d.inventoryNumber})` : d.serialNumber ? ` (SN ${d.serialNumber})` : ''}
                </option>
              ))}
            </Select>
            <Button type="button" size="sm" variant="outline" disabled={!pick} loading={add.isPending} onClick={() => add.mutate(pick)}>
              <Plus /> Verknüpfen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
